import { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FolderOpen } from "lucide-react";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { PageHeader } from "../components/PageHeader";
import { EmptyState, ErrorState } from "../components/ScreenStates";
import { GroupsGrid } from "../components/groups/GroupsGrid";
import { useGroupRowActions } from "../components/groups/useGroupRowActions";

const DEFAULT_SORT = { key: "lastOpened", direction: "desc" } as const;

export function RecentGroupsPage() {
  usePageSetup("Recent Groups", null);
  const navigate = useNavigate();
  const resource = useAsyncResource(() => getApi().getRecentGroups(), "recent", "RecentGroupsPage.load");
  const rowActions = useGroupRowActions((groupId) => resource.setData((current) => current?.filter((group) => group.id !== groupId) ?? null));
  const handleAdd = useCallback(() => navigate("/uw/groups/new"), [navigate]);
  useShortcuts({ add: handleAdd });

  const { state } = resource;
  const groups = state.data ?? [];

  return (
    <>
      <PageHeader title="Recent Groups" subtitle="Your 20 most recently opened groups" />
      {state.kind === "error" && <ErrorState message={state.message} onRetry={resource.reload} />}
      {state.kind !== "error" && (
        <GroupsGrid
          caption="Recently opened groups"
          groups={groups}
          isLoading={state.kind === "loading"}
          defaultSort={DEFAULT_SORT}
          emptyState={
            <EmptyState
              icon={FolderOpen}
              message="No groups yet. Add a group or wait for Skynet to publish one."
              action={
                <Link to="/uw/groups/new" className="inline-flex min-h-[44px] items-center rounded-lg bg-brand-green px-4 py-2 text-sm font-medium text-white hover:bg-brand-green-hover focus:outline-none focus:ring-2 focus:ring-brand-green-50">
                  Add Group
                </Link>
              }
            />
          }
          onEdit={rowActions.handleEdit}
          onCopy={rowActions.handleCopy}
          onDelete={(group) => void rowActions.handleDelete(group)}
        />
      )}
    </>
  );
}
